import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ValidationErrorMapperService } from 'src/app/services/address/validation-error-mapper.service';
import { maskCpfAndCnpj, maskOdometer, maskPhoneNumber } from 'src/assets/functions/information-masks';
import { ApsPermitedScreen } from '../../models/aps-permission/aps-permited-screen';
import { ChoosePlanBuyng } from '../../models/choose-plan/choose-plan-buyng';
import { ClientData } from '../../models/client-data/client-data';
import { ConstantsFordProtect } from '../../models/constants/constants-ford-protect';
import { Patterns } from '../../models/constants/patterns';
import { ContractSummary } from '../../models/contract-summary/contract-summary';
import { ContractPendingPayment } from '../../models/contract/contract-pending-payment';
import { Dealer } from '../../models/dealer/dealer';
import { DealerApsFordDetailAttributes } from '../../models/dealer/dealer-aps-ford-detail-attributes';
import { DealerSeller } from '../../models/dealer/dealer-seller';
import { OperationCode } from '../../models/enum/operation-code';
import { SaleChannel } from '../../models/enum/sale-channel';
import { SalesTypeFordEnum } from '../../models/enum/sales-type-ford';
import { StatusChassi } from '../../models/enum/status-chassi';
import { ProductFull } from '../../models/product/product-full';
import { VinValidate } from "../../models/vin-validate/vin-validate";
import { ApsPermissionService } from '../../services/aps-permission/aps-permission.service';
import { ChoosePlanService } from '../../services/choose-plan/choose-plan.service';
import { ClientDataService } from '../../services/client-data/client-data.service';
import { ContractSummaryService } from '../../services/contract-summary/contract-summary.service';
import { DealerService } from '../../services/dealer/dealer.service';
import { SystemCountryService } from '../../services/system-country/system-country.service';
import { VehicleInformationService } from '../../services/vehicle-information/vehicle-information.service';
import { nationalId } from 'src/assets/validations/cpf_cnpj_validation';

@Component({
	selector: 'app-contract-summary-external',
	templateUrl: './contract-summary-external.component.html',
	styleUrls: ['./contract-summary-external.component.scss'],
	standalone: false
})
export class ContractSummaryExternalComponent implements OnInit {
	private readonly destroyRef = inject(DestroyRef);

	userform!: UntypedFormGroup;
	vinValidate!: VinValidate;
	clientData!: ClientData;
	contractSummary: ContractSummary;
	choosePlanBuyng: ChoosePlanBuyng;
	cdsid = sessionStorage.getItem('userId') || '';
	saleChannel: SaleChannel;
	isSubmitted = false;
	blockedUi = false;
	systemCountry!: string;
	isDealerCodeValid = false;
	private sellerLookupDebounceHandle: ReturnType<typeof setTimeout> | null = null;
	private readonly sellerLookupDebounceMs = 250;
	private dealerLookupDebounceHandle: ReturnType<typeof setTimeout> | null = null;
	private readonly dealerLookupDebounceMs = 250;
	isCtaSuccess = false;
	private formChangeDebounceTimer: ReturnType<typeof setTimeout> | null = null;
	readonly maskCpfAndCnpj = maskCpfAndCnpj;
	readonly maskOdometer = maskOdometer;
	readonly maskPhoneNumber = maskPhoneNumber;

	constructor(private contractSummaryService: ContractSummaryService,
		private vehicleInformationService: VehicleInformationService,
		private clientDataService: ClientDataService,
		private translateService: TranslateService,
		private messageService: MessageService,
		private router: Router,
		private route: ActivatedRoute,
		private fb: UntypedFormBuilder,
		private choosePlanService: ChoosePlanService,
		private apsPermissionService: ApsPermissionService,
		private dealerService: DealerService,
		private systemCountryService: SystemCountryService,
		private validationErrorMapperService: ValidationErrorMapperService
	) {

		this.contractSummary = new ContractSummary();
		this.choosePlanBuyng = new ChoosePlanBuyng();
		this.saleChannel = route.snapshot.paramMap.get('saleChannel') as SaleChannel;
	}

	ngOnInit() {

		window.scrollTo(0, 0);

		this.systemCountry = this.systemCountryService.getSystemCountry();
		this.vinValidate = this.vehicleInformationService.getAllChassiValidation();
		this.clientData = this.clientDataService.getClientData();

		if (this.vinValidate === null || this.vinValidate.validated === StatusChassi.invalid ||
			this.clientDataService.getClientData() === null || this.clientData === null ||
			this.choosePlanService.getChoosePlanBuyng() === null) {
			this.router.navigate(['sales-channel', this.saleChannel]);
		}

		this.userform = this.fb.group({
			'dealerCodigo': new UntypedFormControl('', Validators.required),
			'cdsidSeller': new UntypedFormControl('', Validators.required),
			// 'cpfSeller': new FormControl('', Validators.required),
			'nameSeller': new UntypedFormControl('', Validators.required),
			'foneSeller': new UntypedFormControl('', Validators.required),
			'emailSeller': new UntypedFormControl('', [Validators.required, Validators.pattern(Patterns.EMAIL_STRICT)]),
			'dealerEmail': new UntypedFormControl({ value: '', disabled: true }, [Validators.required, Validators.pattern(Patterns.EMAIL_STRICT)])
		});
		this.setDealerCodeValidationState(false);

		this.apsPermissionService.permissionValidation(ApsPermitedScreen.screenContractReview);

		if (this.contractSummaryService.getContractSummary() !== null) {
			this.contractSummary = this.contractSummaryService.getContractSummary();
		}

		if (this.choosePlanService.getChoosePlanBuyng() !== null) {
			this.choosePlanBuyng = this.choosePlanService.getChoosePlanBuyng();
		}

		// If user confirmed data before (navigated forward), restore saved state without running auto-lookups.
		// Auto-lookups would overwrite user modifications with fresh API data.
		if (this.contractSummaryService.isDealerDataConfirmed()) {
			this.setDealerCodeValidationState(!!this.contractSummary.dealer?.legalName);
			this.syncFormFromContractSummary();
			return;
		}

		// Attempt to restore modified dealer data from previous session
		const hasRestoredSnapshot = this.contractSummaryService.restoreDealerDataSnapshot();

		// If we restored a snapshot from returning user, skip auto-lookups
		if (hasRestoredSnapshot) {
			this.setDealerCodeValidationState(!!this.contractSummary.dealer?.legalName);
			this.syncFormFromContractSummary();
			return;
		}

		this.getSellerAutomatic();
		this.getDealerAutomatic();

		this.syncFormFromContractSummary();
		this.setDealerCodeValidationState(!!this.contractSummary.dealer?.legalName);

		// Listen to form changes for CTA success state
		this.userform.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.onFormChange());
	}

	get isFormComplete(): boolean {
		return this.userform?.valid && this.isDealerCodeValid;
	}

	onFormChange() {
		if (this.formChangeDebounceTimer) {
			clearTimeout(this.formChangeDebounceTimer);
		}
		this.isCtaSuccess = false;

		this.formChangeDebounceTimer = setTimeout(() => {
			this.isCtaSuccess = this.isFormComplete;
		}, 700);
	}

	private setDealerCodeValidationState(isValid: boolean): void {
		this.isDealerCodeValid = isValid;

		const dealerEmailControl = this.userform.get('dealerEmail');
		if (!dealerEmailControl) {
			return;
		}

		if (isValid) {
			dealerEmailControl.enable({ emitEvent: false });
			return;
		}

		dealerEmailControl.reset('', { emitEvent: false });
		dealerEmailControl.disable({ emitEvent: false });

		if (this.contractSummary?.dealer) {
			this.contractSummary.dealer.dealerEmail = '';
		}
	}

	private markControlForValidation(controlName: string): void {
		const control = this.userform.get(controlName);
		if (!control) {
			return;
		}

		control.markAsDirty();
		control.markAsTouched();
		control.markAsPending();
	}

	private focusControlByFormName(controlName: string): boolean {
		const focusElement = (element: HTMLElement | null): boolean => {
			if (!element) {
				return false;
			}

			element.scrollIntoView({ behavior: 'smooth', block: 'center' });
			element.focus();
			setTimeout(() => element.focus(), 60);
			return true;
		};

		const directTargetMap: Record<string, string> = {
			dealerCodigo: '#dealerCodigo',
			dealerEmail: '#dealerEmail',
			cdsidSeller: 'input[formcontrolname="cdsidSeller"]',
			nameSeller: 'input[formcontrolname="nameSeller"]',
			foneSeller: '#phoneSeller',
			emailSeller: 'input[formcontrolname="emailSeller"]'
		};

		const directTarget = directTargetMap[controlName]
			? document.querySelector(directTargetMap[controlName]) as HTMLElement | null
			: null;

		if (focusElement(directTarget)) {
			return true;
		}

		const hostElement = document.querySelector(`[formcontrolname="${controlName}"]`) as HTMLElement | null;
		if (!hostElement) {
			return false;
		}

		const focusTarget = (hostElement.matches('input, textarea, select, button, [tabindex]')
			? hostElement
			: hostElement.querySelector('input, textarea, select, button, [tabindex]')) as HTMLElement | null;

		return focusElement(focusTarget);
	}

	private focusFirstInvalidRequiredField(): void {
		const orderedControls = ['dealerCodigo', 'dealerEmail', 'cdsidSeller', 'nameSeller', 'foneSeller', 'emailSeller'];

		for (const controlName of orderedControls) {
			const control = this.userform.get(controlName);
			if ((control?.invalid || control?.pending) && this.focusControlByFormName(controlName)) {
				return;
			}
		}
	}

	private syncFormFromContractSummary(): void {

		if (!this.userform || !this.contractSummary) {
			return;
		}

		this.userform.patchValue({
			dealerCodigo: this.contractSummary.dealer?.pandAC || '',
			cdsidSeller: this.contractSummary.dealerSeller?.sellerCode || '',
			nameSeller: this.contractSummary.dealerSeller?.name || '',
			foneSeller: this.contractSummary.dealerSeller?.phone || '',
			emailSeller: this.contractSummary.dealerSeller?.email || '',
			dealerEmail: this.contractSummary.dealer?.dealerEmail || ''
		}, { emitEvent: false });
	}

	private syncContractSummaryFromForm(): void {

		if (!this.contractSummary) {
			this.contractSummary = new ContractSummary();
		}

		if (!this.contractSummary.dealer) {
			this.contractSummary.dealer = new Dealer();
		}

		if (!this.contractSummary.dealerSeller) {
			this.contractSummary.dealerSeller = new DealerSeller();
		}

		this.contractSummary.dealer.pandAC = this.userform.get('dealerCodigo')?.value || '';
		this.contractSummary.dealer.dealerEmail = this.userform.get('dealerEmail')?.value || '';
		this.contractSummary.dealerSeller.sellerCode = this.userform.get('cdsidSeller')?.value || '';
		this.contractSummary.dealerSeller.name = this.userform.get('nameSeller')?.value || '';
		this.contractSummary.dealerSeller.phone = this.userform.get('foneSeller')?.value || '';
		this.contractSummary.dealerSeller.email = this.userform.get('emailSeller')?.value || '';
	}

	buttonComeBack() {
		this.syncContractSummaryFromForm();
		this.contractSummaryService.saveDealerDataSnapshot();
		this.router.navigate(['client-data', this.saleChannel]);
	}

	getDealer(_event: Event) {
		if (this.dealerLookupDebounceHandle) {
			clearTimeout(this.dealerLookupDebounceHandle);
		}

		this.dealerLookupDebounceHandle = setTimeout(() => {
			this.dealerLookupDebounceHandle = null;
			this.runDealerLookup();
		}, this.dealerLookupDebounceMs);
	}

	private runDealerLookup(): void {
		this.syncContractSummaryFromForm();
		const searchedDealerCode = (this.contractSummary.dealer?.pandAC || '').trim().toUpperCase();

		if (!searchedDealerCode) {
			this.setDealerCodeValidationState(false);
			return;
		}

		this.contractSummary.dealer.pandAC = searchedDealerCode;
		this.contractSummary.dealer.geoSalesCode = this.systemCountry;
		this.contractSummary.dealer.legalName = '';
		this.contractSummary.dealer.dealerEmail = '';
		this.setDealerCodeValidationState(false);
		this.syncFormFromContractSummary();

		this.dealerService.getDealer(this.contractSummary.dealer)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((response: Dealer) => {

				if (response) {
					this.contractSummary.dealer = response;
					this.setDealerCodeValidationState(true);
					this.syncFormFromContractSummary();
					this.contractSummaryService.setContractSummary(this.contractSummary);
				} else {
					this.handleDealerNotFound(searchedDealerCode);
				}

			}, (err) => {

				if (err.status === 404) {
					this.handleDealerNotFound(searchedDealerCode);

				} else if (err.status === 401) {

					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
					});

				} else {

					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
					});
				}

			});
	}

	private handleDealerNotFound(searchedDealerCode: string): void {
		this.contractSummary.dealer = new Dealer();
		this.contractSummary.dealer.pandAC = searchedDealerCode;
		this.contractSummary.dealer.geoSalesCode = this.systemCountry;
		this.setDealerCodeValidationState(false);
		this.syncFormFromContractSummary();
		this.contractSummaryService.setContractSummary(this.contractSummary);

		this.messageService.add({
			severity: 'error',
			summary: this.translateService.instant('TRANSLATE.ERROR'),
			detail: this.translateService.instant('TRANSLATE.DEALER.NOT.FOUND.ACTION')
		});

		setTimeout(() => {
			(document.getElementById('dealerCodigo') as HTMLInputElement | null)?.focus();
		}, 0);
	}

	getDealerSeller(_event: Event) {
		if (this.sellerLookupDebounceHandle) {
			clearTimeout(this.sellerLookupDebounceHandle);
		}

		this.sellerLookupDebounceHandle = setTimeout(() => {
			this.sellerLookupDebounceHandle = null;
			this.runDealerSellerLookup();
		}, this.sellerLookupDebounceMs);
	}

	private runDealerSellerLookup(): void {
		this.syncContractSummaryFromForm();
		const searchedSellerCode = (this.contractSummary.dealerSeller?.sellerCode || '').trim().toUpperCase();

		if (!searchedSellerCode) {
			return;
		}

		this.contractSummary.dealerSeller.sellerCode = searchedSellerCode;
		this.syncFormFromContractSummary();

		this.dealerService.getDealerSeller(this.contractSummary.dealerSeller)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((response: DealerSeller | null | undefined) => {

				if (response) {
					this.contractSummary.dealerSeller = response;

					const processedPhone = (response.phone || '').replace(/[\s()]/g, '');
					this.contractSummary.dealerSeller.phone = processedPhone;
					this.syncFormFromContractSummary();
					this.contractSummaryService.setContractSummary(this.contractSummary);
				} else {
					this.handleSellerNotFound(searchedSellerCode);
				}

			}, (err) => {

				if (err.status === 404) {
					this.handleSellerNotFound(searchedSellerCode);

				} else if (err.status === 401) {

					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
					});

				} else {

					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
					});
				}

			});
	}

	private handleSellerNotFound(searchedSellerCode: string): void {
		this.contractSummary.dealerSeller = new DealerSeller();
		this.contractSummary.dealerSeller.sellerCode = searchedSellerCode;
		this.syncFormFromContractSummary();
		this.contractSummaryService.setContractSummary(this.contractSummary);

		this.messageService.add({
			severity: 'warn',
			summary: this.translateService.instant('TRANSLATE.ATTENTION'),
			detail: this.translateService.instant('TRANSLATE.SELLER.NOT.FOUND.ACTION')
		});

		setTimeout(() => {
			(document.getElementById('phoneSeller') as HTMLInputElement | null)?.focus();
		}, 0);
	}

	getSellerAutomatic() {

		if (this.cdsid) {
			this.contractSummary.dealerSeller.sellerCode = this.cdsid;
			this.dealerService.getDealerSeller(this.contractSummary.dealerSeller)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe((response: DealerSeller) => {
					this.contractSummary.dealerSeller = response;
					this.syncFormFromContractSummary();
				}, (err) => {

					if (err.status === 404) {

						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.SELLER.NOT.FOUND')
						});

					} else if (err.status === 401) {

						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
						});

					} else {

						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
						});
					}

				});
		}
	}

	/*
	* captura o cdsid que está na sessão;
	* informa ele no serviço de DLS;
	* se houver um response, verifica se tem um sitecode e atribui;
	* se não houver response, atribui o site code 00100 (FORD MOTOR COMPANY BRASIL LTDA) automaticamente;
	*/
	getDealerAutomatic() {

		this.contractSummary.dealerSeller.name = this.cdsid;
		this.dealerService.getDealerName(this.contractSummary.dealerSeller)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((response: DealerApsFordDetailAttributes) => {
				if (response === null) {
					this.contractSummary.dealer.pandAC = ConstantsFordProtect.PANDAC_BRA_DEFAULT;
					if (this.systemCountry === ConstantsFordProtect.ARG) {
						this.contractSummary.dealer.pandAC = ConstantsFordProtect.PANDAC_ARG_DEFAULT;
					}
				} else {
					this.contractSummary.dealer.pandAC = response.attributeValue;
				}
				this.contractSummary.dealer.geoSalesCode = this.systemCountry;
				this.dealerService.getDealer(this.contractSummary.dealer)
					.pipe(takeUntilDestroyed(this.destroyRef))
					.subscribe((response: Dealer) => {
						if (response) {
							this.contractSummary.dealer = response;
							this.setDealerCodeValidationState(true);
							this.syncFormFromContractSummary();
						} else {
							this.handleDealerNotFound(this.contractSummary.dealer.pandAC || '');
						}
					});
			});
	}

	save(): void {
		let hasValidationError = false;
		const requiredControls = ['dealerCodigo', 'cdsidSeller', 'nameSeller', 'foneSeller', 'emailSeller'];

		if (this.isDealerCodeValid) {
			requiredControls.push('dealerEmail');
		}

		for (const controlName of requiredControls) {
			if (this.userform.get(controlName)?.invalid) {
				this.markControlForValidation(controlName);
				hasValidationError = true;
			}
		}

		if (!this.isDealerCodeValid) {
			this.markControlForValidation('dealerCodigo');
			this.messageService.add({
				severity: 'error',
				summary: this.translateService.instant('TRANSLATE.ERROR'),
				detail: this.translateService.instant('TRANSLATE.DEALER.NOT.FOUND.ACTION')
			});
			this.focusControlByFormName('dealerCodigo');
			return;
		}

		if (hasValidationError || this.userform.invalid) {
			this.focusFirstInvalidRequiredField();
			return;
		}

		this.syncContractSummaryFromForm();
		this.isSubmitted = true;
		this.blockedUi = true;
		this.contractSummaryService.confirmDealerData();
		this.contractSummaryService.setContractSummary(this.contractSummary);

		this.contractSummaryService.saveContractPendingPayment(this.setContractPendingPayment())
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((_response: ContractPendingPayment) => {

				this.router.navigate(['home']);
				this.messageService.add({
					severity: 'success',
					summary: this.translateService.instant('TRANSLATE.SUCCESS'),
					detail: this.translateService.instant('TRANSLATE.PAYMENT.PENDING')
				});
				this.vinValidate = new VinValidate();
				this.choosePlanBuyng = new ChoosePlanBuyng();
				this.clientData = new ClientData();
				this.contractSummary = new ContractSummary();

				this.blockedUi = false;
				this.isSubmitted = false;

			}, (err) => {

				if (err.status === 400 || err.status === 404 || err.status === 500) {
					// Attempt to map backend error to specific i18n key
					const mappedError = this.validationErrorMapperService.mapBackendErrorToI18nKey(err);
					const errorKey = mappedError.key.startsWith('TRANSLATE.ERROR.CEP.')
						? 'TRANSLATE.CONTRACT.CANNOT.BE.SAVE.CONTACT.ADM'
						: mappedError.key;
					const errorDetail = this.translateService.instant(errorKey);

					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: errorDetail
					});

				} else if (err.status === 401) {

					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
					});

				} else {

					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
					});
				}

				this.isSubmitted = false;
				this.blockedUi = false;
			});
	}

	private setContractPendingPayment(): ContractPendingPayment {

		const contract = new ContractPendingPayment();
		contract.country = ConstantsFordProtect.BRA;
		contract.operation = OperationCode.CAR;
		contract.status = ConstantsFordProtect.CONTRACT_ATIVO_CODE;
		contract.user = ConstantsFordProtect.EMISSION_USER;
		contract.saleDate = new Date();
		contract.startDate = this.contractStartDate();
		contract.saleType = SalesTypeFordEnum.TELEMARKETING_CODE; //Telemarketing
		contract.finalPrice = this.choosePlanBuyng.totalPrice;

		if (this.vinValidate.odometro !== null) {
			contract.odometerStart = this.vinValidate.odometro;
		} else {
			contract.odometerStart = 1;
		}

		contract.vin.vin = this.vinValidate.vin;
		contract.vin.cvvgDate = this.contractStartDate();
		contract.vin.factoryWarrantyEndDate = null;
		contract.vin.idVinCategoryVersion = this.vinValidate.idVinCategoryVersion;

		contract.payments.totalValue = this.choosePlanBuyng.totalPrice;

		contract.dealers.code = this.contractSummary.dealer.dealerCode;
		contract.dealers.pAndAC = this.contractSummary.dealer.pandAC;
		contract.dealers.divisionCode = this.contractSummary.dealer.divisionCode;
		contract.dealers.oldRegionCode = this.contractSummary.dealer.oldRegionCode;
		contract.dealers.geoSalesCode = this.contractSummary.dealer.geoSalesCode;
		contract.dealers.subCode = this.contractSummary.dealer.dealerSubCode;
		contract.dealers.name = this.contractSummary.dealer.dealerName;
		contract.dealers.email = this.contractSummary.dealer.dealerEmail;
		contract.dealers.phone = this.contractSummary.dealer.dealerPhone;

		contract.sellers.code = this.contractSummary.dealerSeller.sellerCode;
		contract.sellers.name = this.contractSummary.dealerSeller.name;
		contract.sellers.phone = this.contractSummary.dealerSeller.phone;
		contract.sellers.email = this.contractSummary.dealerSeller.email;
		contract.customers.country = this.clientData.countryCode;

		contract.customers.documentType =  nationalId(this.clientData.nationalId, this.systemCountry);
		contract.customers.documentNumber = this.clientData.nationalId;
		contract.customers.name = this.clientData.firstname;
		contract.customers.phone = this.clientData.phone1;
		contract.customers.email = this.clientData.email1;
		contract.customers.lastName = this.clientData.lastname;
		contract.customers.customerSocialName = this.clientData.customerSocialName;
		contract.customers.cep = this.clientData.zip;
		contract.customers.streetName = this.clientData.address;
		contract.customers.streetNumber = this.clientData.number;
		contract.customers.streetComplement = this.clientData.complement;
		contract.customers.district = this.clientData.district;
		contract.customers.state = this.clientData.stateCode;
		contract.customers.city = this.clientData.city;

		if (this.choosePlanBuyng.extension.coverageCategoryId > 0 && this.choosePlanBuyng.extension.productId > 0) {
			const productExtension = new ProductFull();
			productExtension.coverageId = this.choosePlanBuyng.extension.coverageCategoryId;
			productExtension.productId = this.choosePlanBuyng.extension.productId;
			productExtension.productPolicyId = this.choosePlanBuyng.extension.productPolicyKey;
			productExtension.priceBookId = this.choosePlanBuyng.extension.pricebookKey;
			contract.products.push(productExtension);
		}

		if (this.choosePlanBuyng.maintenance.coverageCategoryId > 0 && this.choosePlanBuyng.maintenance.productId > 0) {
			const productFullMaintenance = new ProductFull();
			productFullMaintenance.coverageId = this.choosePlanBuyng.maintenance.coverageCategoryId;
			productFullMaintenance.productId = this.choosePlanBuyng.maintenance.productId;
			productFullMaintenance.productPolicyId = this.choosePlanBuyng.maintenance.productPolicyKey;
			productFullMaintenance.priceBookId = this.choosePlanBuyng.maintenance.pricebookKey;
			contract.products.push(productFullMaintenance);
		}

		if (this.choosePlanBuyng.part.coverageCategoryId > 0 && this.choosePlanBuyng.part.productId > 0) {
			const productFullPart = new ProductFull();
			productFullPart.coverageId = this.choosePlanBuyng.part.coverageCategoryId;
			productFullPart.productId = this.choosePlanBuyng.part.productId;
			productFullPart.productPolicyId = this.choosePlanBuyng.part.productPolicyKey;
			productFullPart.priceBookId = this.choosePlanBuyng.part.pricebookKey;
			contract.products.push(productFullPart);
		}

		return contract;
	}

	contractStartDate(): Date {
		let date = new Date();
		if (this.vinValidate.warrantyStartDate !== null) {
			date = this.vinValidate.warrantyStartDate;
		}
		return date;
	}

	getCurrencyCodeByCountry(): string {
		switch (this.systemCountry) {
			case ConstantsFordProtect.ARG:
				return 'ARS';
			case ConstantsFordProtect.CHL:
				return 'CLP';
			case ConstantsFordProtect.COL:
				return 'COP';
			default:
				return 'BRL';
		}
	}

	getCurrencyDisplayByCountry(): string {
		switch (this.systemCountry) {
			case ConstantsFordProtect.ARG:
			case ConstantsFordProtect.CHL:
			case ConstantsFordProtect.COL:
				return '$ ';
			default:
				return 'symbol';
		}
	}

	getLocaleByCountry(): string {
		switch (this.systemCountry) {
			case ConstantsFordProtect.ARG:
			case ConstantsFordProtect.CHL:
			case ConstantsFordProtect.COL:
				return 'en-US';
			default:
				return ConstantsFordProtect.PT_BR;
		}
	}

	getSummaryDocumentValue(documentValue: string): string {
		if (this.systemCountry === ConstantsFordProtect.BRA) {
			return maskCpfAndCnpj(documentValue);
		}

		return (documentValue || '').trim();
	}

	getDocumentLabel(cpfCnpj: string): string {
		switch (this.systemCountry) {
			case ConstantsFordProtect.ARG:
				return 'DNI/CUIT:';
			case ConstantsFordProtect.CHL:
				return 'RUN/RUT:';
			case ConstantsFordProtect.COL:
				return 'Cédula/NIT:';
			case ConstantsFordProtect.VEN:
				return 'Cédula/RIF:';
		}

		const nationalIdDigitsLength = this.getNationalIdDigits(cpfCnpj).length;

		if (nationalIdDigitsLength === 11) {
			return 'CPF:';
		}

		if (nationalIdDigitsLength === 14) {
			return 'CNPJ:';
		}

		// Se não há dígitos suficientes, verifica pelo comprimento total
		if (nationalIdDigitsLength < 11) {
			const totalLength = (cpfCnpj || '').replace(/\s/g, '').length;

			if (totalLength === 14) {
				return 'CNPJ:';
			} else if (totalLength === 11) {
				return 'CPF:';
			}
		}

		return 'CPF/CNPJ:';
	}

	private getNationalIdDigits(cpfCnpj: string): string {
		return (cpfCnpj || '').replace(/\D/g, '');
	}

}